import { useState, useEffect, forwardRef, useImperativeHandle } from 'react'
import {
  TextInput,
  Toggle,
  DatePicker,
  DatePickerInput,
  Stack,
  Row,
  Column,
  Button,
} from '@carbon/react'
import { Edit } from '@carbon/icons-react'
import {
  ModalWithButton,
  ModalFormContentProps,
  ModalFormHandle,
} from '@sphere/shared/ui/common'
import type {
  ActivityTeachingAssignment,
  ActivityUserAssignment,
} from '@sphere/learning/domain'
import styles from '../global-assignment-edit-modal/GlobalAssignmentEditModal.module.scss'

// ============================================================================
// Types
// ============================================================================

export interface AssignmentFormData {
  availableFrom?: Date
  dueAt?: Date
  maxAttempts?: number
  showSolutions: boolean
  randomizeOrder?: boolean
  isVisible?: boolean
}

interface ActivityAssignmentFormContentProps extends ModalFormContentProps {
  assignment: ActivityTeachingAssignment | ActivityUserAssignment
  isTeaching: boolean
}

// ============================================================================
// Form Content Component
// ============================================================================

const ActivityAssignmentFormContent = forwardRef<
  ModalFormHandle<AssignmentFormData>,
  ActivityAssignmentFormContentProps
>(({ closeModal, assignment, isTeaching }, ref) => {
  const [formData, setFormData] = useState<AssignmentFormData>({
    availableFrom: undefined,
    dueAt: undefined,
    maxAttempts: undefined,
    showSolutions: false,
    randomizeOrder: false,
    isVisible: true,
  })

  // Initialise le formulaire avec les données de l'assignation
  useEffect(() => {
    if (assignment) {
      if (isTeaching) {
        const teachingAssignment = assignment as ActivityTeachingAssignment
        setFormData({
          availableFrom: assignment.availableFrom
            ? new Date(assignment.availableFrom)
            : undefined,
          dueAt: assignment.dueAt ? new Date(assignment.dueAt) : undefined,
          maxAttempts: assignment.maxAttempts ?? 1,
          showSolutions: assignment.showSolutions ?? false,
          randomizeOrder: teachingAssignment.randomizeOrder ?? false,
          isVisible: teachingAssignment.isVisible ?? true,
        })
      } else {
        setFormData({
          availableFrom: assignment.availableFrom
            ? new Date(assignment.availableFrom)
            : undefined,
          dueAt: assignment.dueAt ? new Date(assignment.dueAt) : undefined,
          maxAttempts: assignment.maxAttempts ?? 1,
          showSolutions: assignment.showSolutions ?? false,
        })
      }
    }
  }, [assignment, isTeaching])

  // Expose getData() au parent via la ref
  useImperativeHandle(ref, () => ({
    getData: () => formData,
  }))

  return (
    <Stack gap={6}>
      <p style={{ marginBottom: '1rem' }}>
        {isTeaching ? 'Assignation de classe/groupe' : 'Assignation élève'}
      </p>

      {/* Dates et tentatives */}
      <div className={styles.grid}>
        <DatePicker
          locale="fr"
          dateFormat="d/m/Y"
          datePickerType="single"
          value={formData.availableFrom}
          onChange={(dates: Date[]) => {
            if (dates && dates[0]) {
              const date = new Date(dates[0])
              date.setHours(0, 0, 0, 0)
              setFormData(prev => ({ ...prev, availableFrom: date }))
            }
          }}
        >
          <DatePickerInput
            id={`edit-availableFrom-${assignment.id}`}
            labelText="Disponible du"
            placeholder="jj/mm/aaaa"
          />
        </DatePicker>
        <DatePicker
          locale="fr"
          dateFormat="d/m/Y"
          datePickerType="single"
          value={formData.dueAt}
          onChange={(dates: Date[]) => {
            if (dates && dates[0]) {
              const date = new Date(dates[0])
              date.setHours(23, 59, 59, 999)
              setFormData(prev => ({ ...prev, dueAt: date }))
            }
          }}
        >
          <DatePickerInput
            id={`edit-dueAt-${assignment.id}`}
            labelText="À rendre pour"
            placeholder="jj/mm/aaaa"
          />
        </DatePicker>
        <TextInput
          id={`edit-maxAttempts-${assignment.id}`}
          labelText="Tentatives max"
          type="number"
          min="1"
          value={formData.maxAttempts?.toString() || ''}
          onChange={e => {
            setFormData(prev => ({
              ...prev,
              maxAttempts: e.target.value
                ? parseInt(e.target.value)
                : undefined,
            }))
          }}
        />
      </div>

      {/* Options avec Toggles */}
      <Stack gap={5}>
        <Row>
          <Column>
            <Toggle
              id={`edit-showSolutions-${assignment.id}`}
              labelText="Afficher les solutions"
              toggled={formData.showSolutions}
              onToggle={checked =>
                setFormData(prev => ({ ...prev, showSolutions: checked }))
              }
            />
          </Column>
        </Row>

        {isTeaching && (
          <>
            <Row>
              <Column>
                <Toggle
                  id={`edit-randomizeOrder-${assignment.id}`}
                  labelText="Ordre aléatoire"
                  toggled={formData.randomizeOrder ?? false}
                  onToggle={checked =>
                    setFormData(prev => ({ ...prev, randomizeOrder: checked }))
                  }
                />
              </Column>
            </Row>
            <Row>
              <Column>
                <Toggle
                  id={`edit-isVisible-${assignment.id}`}
                  labelText="Visible pour les élèves"
                  toggled={formData.isVisible ?? true}
                  onToggle={checked =>
                    setFormData(prev => ({ ...prev, isVisible: checked }))
                  }
                />
              </Column>
            </Row>
          </>
        )}
      </Stack>
    </Stack>
  )
})

ActivityAssignmentFormContent.displayName = 'ActivityAssignmentFormContent'

// ============================================================================
// Main Component with Button
// ============================================================================

interface ActivityAssignmentEditButtonProps {
  assignment: ActivityTeachingAssignment | ActivityUserAssignment
  onSave: (
    id: string,
    type: 'teaching' | 'user',
    data: AssignmentFormData
  ) => Promise<void>
  isSaving: boolean
}

/**
 * Bouton d'édition avec modal intégré pour modifier une assignation.
 * Utilise le nouveau pattern ModalWithButton.
 */
export function ActivityAssignmentEditButton({
  assignment,
  onSave,
  isSaving,
}: ActivityAssignmentEditButtonProps) {
  const isTeaching = 'teachingId' in assignment

  const handleSave = async (
    data: AssignmentFormData,
    closeModal: () => void
  ) => {
    const type = isTeaching ? 'teaching' : 'user'
    await onSave(assignment.id, type, data)
    closeModal()
  }

  // Créer un wrapper du FormContent qui passe les props supplémentaires
  const FormContentWrapper = forwardRef<
    ModalFormHandle<AssignmentFormData>,
    ModalFormContentProps
  >((props, ref) => (
    <ActivityAssignmentFormContent
      {...props}
      ref={ref}
      assignment={assignment}
      isTeaching={isTeaching}
    />
  ))
  FormContentWrapper.displayName = 'FormContentWrapper'

  return (
    <ModalWithButton<AssignmentFormData>
      buttonLabel="Modifier"
      buttonIcon={Edit}
      iconDescription="Modifier"
      buttonSize="sm"
      buttonKind="ghost"
      disabled={isSaving}
      modalHeading="Modifier l'assignation"
      modalSize="md"
      primaryButtonText={isSaving ? 'Enregistrement...' : 'Enregistrer'}
      secondaryButtonText="Annuler"
      primaryButtonDisabled={isSaving}
      onPrimarySubmit={handleSave}
      FormContent={FormContentWrapper}
    />
  )
}
